import React, { useEffect, useState, useCallback } from "react";
import { database, auth } from "../firebaseConfig";
import { ref, onValue, orderByChild, startAt, limitToFirst, get } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  getCountFromServer
} from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import WorldDbTable from "./WorldDbTable";
import WorldTableTwo from "./WorldTableTwo";
import WorldSettings from "./WorldSettings";
import WorldInbox from "./WorldInbox";
import SmartInbox from "./SmartInbox";
import WorldDbUploader from "./WorldDbUploader";
import WorldDbCreator from "./WorldDbCreator";
import WorldDbHeader from "./WorldDbHeader";
import EmailSentNotifier from "./EmailSentNotifier";
import OfferCreator from "./OfferCreator/OfferCreator"; 
import WorldDbImportFilter from "./WorldDbImportFilter";
import WorldDbMigrationModal from "./WorldDbMigrationModal";
import "./WorldDb.css";
import NewWorldDbTable from "./NewWorldDbTable";
import useDebounce from "../hooks/use-debounce";

// Einfacher Loading-Spinner
const LoadingSpinner = () => (
  <div className="simple-loading-overlay">
    <img src="/img/salesgod_dark.webp" alt="SalesGod Logo" className="simple-loading-spinner" />
    <img src="/img/BonusClub.png" alt="BonusClub Logo" className="simple-loading-logo" />
    <p></p>
  </div>
);

// Coming Soon Komponente für Payouts
const ComingSoon = () => (
  <div className="coming-soon-container">
    <div className="coming-soon-icon">
      <img src="/img/BonusClub_Logo.webp" alt="Coming Soon" />
    </div>
    <h2 className="coming-soon-title">Coming Soon</h2>
    <p className="coming-soon-text">This feature will be available soon.</p>
  </div>
);

const UploadButton = ({ onUpload, isLoading }) => {
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div className="upload-button-container">
      <label className={`upload-button ${isLoading ? 'loading' : ''}`}>
        {!isLoading && 'Upload Database'}
        <input
          type="file"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept=".csv,.xlsx,.xls"
          disabled={isLoading}
        />
      </label>
    </div>
  );
};

const WorldDb = () => {
  const [dbData, setDbData] = useState([]);
  const [fullDbData, setFullDbData] = useState([]); // Speichert alle Daten für Filterung
  const [filteredData, setFilteredData] = useState([]); // Alle gefilterten Daten
  const [displayedData, setDisplayedData] = useState([]); // Nur die angezeigten Daten (mit Limit)
  const [columns, setColumns] = useState([]);
  const [userId, setUserId] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeView, setActiveView] = useState("tableOne");
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(100); // Anzahl der anzuzeigenden Einträge
  const [hasMoreData, setHasMoreData] = useState(false); // Gibt es weitere Daten zum Laden?
  const [currentFilter, setCurrentFilter] = useState(null); // Speichert den aktuellen Filter
  const [searchTerm, setSearchTerm] = useState(""); // Speichert den Suchbegriff
  const [statusFilterMode, setStatusFilterMode] = useState("all"); // Speichert den Status-Filter
  const [importFilterMode, setImportFilterMode] = useState("all"); // Neuer State für Import-Filter
  const [importOptions, setImportOptions] = useState([]); // Verfügbare Import-Namen
  const [sortField, setSortField] = useState(null); // Speichert das Sortierfeld
  const [sortDirection, setSortDirection] = useState(null); // Speichert die Sortierrichtung
  const [todayContactedCount, setTodayContactedCount] = useState(0); // Speichert die Anzahl der heute angeschriebenen Kontakte
  
  const [emailNotifier, setEmailNotifier] = useState({
    status: '', // 'success' oder 'error'
    message: '',
    scheduledTime: null,
    recipientEmail: null
  });

  const [needsMigration, setNeedsMigration] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);

  const [reloadFlag, setReloadFlag] = useState(0);

  const [lastVisible, setLastVisible] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [totalContactCount, setTotalContactCount] = useState(null); // <--- NEU

  // Debounce für effiziente Firestore-Queries
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const reloadData = useCallback(() => {
    setReloadFlag(flag => flag + 1);
    fetchTotalContactCount(); // Erzwinge das Neuzählen der Kontakte
  }, [userId]); // Abhängigkeit von userId hinzufügen

  // Aktualisierte buildFirestoreQuery Funktion für komplexere Filter
  const buildFirestoreQuery = (baseQuery, filters) => {
    let q = baseQuery;
    
    // Status-Filter
    if (filters.statusMode && filters.statusMode !== "all") {
      const status = filters.statusMode;
      
      if (status === "Not Contacted") {
        q = query(q, where("lead_contacted", "in", [null, "", "Not Contacted"]));
      } else {
        // Für alle anderen Status filtern wir nach dem exakten Wert.
        q = query(q, where("lead_contacted", "==", status));
      }
    }
    
    // Import-Filter
    if (filters.importMode && filters.importMode !== "all") {
      q = query(q, where("import_name", "==", filters.importMode));
    }
    
    // Such-Filter (nur für E-Mail und Business Name)
    if (filters.searchTerm && filters.searchTerm.trim() !== '') {
      const searchTerm = filters.searchTerm.toLowerCase();
      // Firestore kann nicht effizient auf mehreren Feldern gleichzeitig suchen.
      // Wir konzentrieren uns auf das wichtigste Feld: E-Mail.
      // Für eine Volltextsuche wären externe Tools wie Algolia oder Typesense nötig.
      q = query(q, 
        where("lead_email", ">=", searchTerm),
        where("lead_email", "<=", searchTerm + '\uf8ff')
      );
    }
    
    return q;
  };

  // Aktualisierter useEffect für Daten-Loading
  useEffect(() => {
    if (!userId) {
      setDbData([]);
      setFullDbData([]);
      setFilteredData([]);
      setDisplayedData([]);
      setColumns([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    loadData();
  }, [
    userId, 
    reloadFlag, 
    sortField, 
    sortDirection, 
    displayLimit,
    statusFilterMode,
    importFilterMode,
    debouncedSearchTerm
  ]);

  // Aktualisierte loadData Funktion mit Filterung
  const loadData = async () => {
    if (!userId) {
      setDbData([]);
      setFullDbData([]);
      setFilteredData([]);
      setDisplayedData([]);
      setColumns([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Basis-Query erstellen
      let q = collection(firestore, `World/${userId}/db`);
      
      // Filter anwenden
      q = buildFirestoreQuery(q, {
        statusMode: statusFilterMode,
        importMode: importFilterMode,
        searchTerm: debouncedSearchTerm
      });
      
      // Sortierung hinzufügen
      if (sortField) {
        q = query(q, orderBy(sortField, sortDirection));
      }
      
      // Limit für die erste Seite
      q = query(q, limit(displayLimit));
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const arrayData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        
        // Spalten bestimmen
        if (arrayData.length > 0) {
          const columnKeys = [...new Set(arrayData.flatMap(Object.keys))];
          const formattedColumns = columnKeys.map((key) => ({
            key,
            label: key.replace(/_/g, " ").toUpperCase(),
          }));
          setColumns(formattedColumns);
        }
        
        // States aktualisieren
        setFullDbData(arrayData);
        setFilteredData(arrayData);
        setDisplayedData(arrayData);
        setDbData(arrayData);
        
        // Pagination-Status setzen
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMoreData(snapshot.docs.length === displayLimit);
      } else {
        setFullDbData([]);
        setFilteredData([]);
        setDisplayedData([]);
        setDbData([]);
        setHasMoreData(false);
      }
      
      setLoading(false);
      setIsInitialLoad(false);
    } catch (error) {
      console.error("Fehler beim Laden der Firestore-Daten:", error);
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Aktualisierte loadMoreData Funktion mit Filterung
  const loadMoreData = async () => {
    if (!lastVisible || !hasMoreData || loadingMore) return;
  
    setLoadingMore(true);
    
    try {
      // Basis-Query erstellen
      let q = collection(firestore, `World/${userId}/db`);
      // Filter anwenden
      q = buildFirestoreQuery(q, {
        statusMode: statusFilterMode,
        importMode: importFilterMode,
        searchTerm: debouncedSearchTerm
      });
      // Sortierung hinzufügen
      if (sortField) {
        q = query(q, orderBy(sortField, sortDirection));
      }
      // Pagination mit startAfter
      q = query(q, startAfter(lastVisible), limit(100)); // Immer 100 pro Page
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const newData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setFullDbData(prev => [...prev, ...newData]);
        setFilteredData(prev => [...prev, ...newData]);
        setDisplayedData(prev => [...prev, ...newData]);
        setDbData(prev => [...prev, ...newData]);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMoreData(snapshot.docs.length === 100);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Fehler beim Laden weiterer Daten:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Berechne die Anzahl der heute kontaktierten Kontakte basierend auf allen gefilterten Daten
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Setze auf Beginn des Tages
    
    const contactedCount = filteredData.filter(row => {
      // Sicherstellen, dass row existiert, bevor wir auf Eigenschaften zugreifen
      if (!row) return false;
      
      // Sicherstellen, dass lead_last_email und timestamp existieren
      if (row.lead_last_email && row.lead_last_email.timestamp) {
        const emailTimestamp = parseInt(row.lead_last_email.timestamp);
        if (!isNaN(emailTimestamp)) {
          const emailDate = new Date(emailTimestamp);
          return emailDate >= today;
        }
      }
      return false;
    }).length;
    
    setTodayContactedCount(contactedCount);
  }, [filteredData]);

  // Auth-Überwachung (holt die User-ID)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Prüfe, ob Migration benötigt wird
  useEffect(() => {
    if (!userId) return;

    const checkMigrationStatus = async () => {
      console.log('[Migration Check] userId:', userId);
      try {
        // Prüfe, ob Daten in der Realtime Database existieren
        const dbRef = ref(database, `/World/${userId}/db`);
        const snapshot = await get(dbRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val() || {};
          const count = Object.keys(data).length;
          console.log('[Migration Check] Realtime DB contact count:', count);
          setNeedsMigration(count > 0);
          if (count > 0) {
            const firstKeys = Object.keys(data).slice(0, 3);
            console.log('[Migration Check] Sample entries:');
            firstKeys.forEach(key => {
              console.log(`- ${key}:`, data[key]);
            });
          }
        } else {
          setNeedsMigration(false);
        }
      } catch (error) {
        console.error("Fehler beim Prüfen des Migrationsstatus:", error);
      }
    };

    checkMigrationStatus();
  }, [userId]);

  // Handler für erfolgreiche Migration
  const handleMigrationComplete = () => {
    setNeedsMigration(false);
    setMigrationComplete(true);
    // Lade die Daten neu
    window.location.reload();
  };

  // Handler-Funktion für Sortierungsänderungen von den Tabellenkomponenten
  const handleSortChange = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Funktion zum Zurücksetzen des Notifiers
  const resetEmailNotifier = () => {
    setEmailNotifier({ status: '', message: '', scheduledTime: null, recipientEmail: null });
  };

  // Funktion, die von Kindkomponenten aufgerufen werden kann, um den Notifier zu aktualisieren
  // oder den Status direkt zu aktualisieren
  const handleEmailResult = (status, message, rowData, scheduledTime, recipientEmail) => {
    // Stelle sicher, dass scheduledTime ein Zeitstempel ist
    let validScheduledTime = scheduledTime;
    if (scheduledTime && typeof scheduledTime === 'string') {
      try {
        validScheduledTime = new Date(scheduledTime).getTime();
        if (isNaN(validScheduledTime)) {
          validScheduledTime = null;
        }
      } catch (e) {
        validScheduledTime = null;
      }
    }
    
    // Wenn es sich um ein Status-Update handelt
    if (status === 'status-update' && message) {
      // Prüfe, ob das Update bereits von einer Unterkomponente verarbeitet wurde
      if (message._processed) {
        return;
      }
      
      // Check ob dies ein kritisches Update ist, das Flackern verhindern soll
      const isCriticalUpdate = message._critical_update === true;
      const shouldPreventRefresh = message._prevent_refresh === true;
      
      // Optimistisches lokales Update - direkt den Status ändern, ohne auf Firebase zu warten
      // Das verhindert das "Wackeln" oder Neuladen der Tabelle
      setFullDbData(prevData => {
        // Finde den Eintrag mit der entsprechenden ID
        const itemIndex = prevData.findIndex(item => item && item.id === message.id);
        
        // Wenn der Eintrag gefunden wurde, aktualisiere ihn
        if (itemIndex !== -1) {
          // Kopiere das aktuelle Array
          const updatedData = [...prevData];
          
          // Erstelle ein neues Objekt mit den aktualisierten Werten
          updatedData[itemIndex] = {
            ...updatedData[itemIndex],
            [message.field]: message.value,
            lead_contacted: message.value,
            lead_last_email: {
              ...updatedData[itemIndex].lead_last_email,
              timestamp: message.timestamp,
              status: message.value
            },
            // Setze Zeitstempel für die letzte Änderung
            updated_at: message.timestamp,
            last_contact_date: new Date(message.timestamp).toISOString(),
            // Markiere das Update als lokal, um unnötige Neusynchronisierungen zu vermeiden
            _local_status_update: true,
            // Weitere Flags für bessere Kontrolle
            _optimistic_update: true,
            _client_update_timestamp: message.timestamp,
            // Wenn dies ein kritisches Update ist, verwende zusätzliche Flags
            ...(isCriticalUpdate ? {
              _critical_update: true,
              _prevent_refresh: true
            } : {})
          };
          
          return updatedData;
        }
        
        return prevData;
      });
      
      // Stellen wir auch sicher, dass die gefilterten und angezeigten Daten aktualisiert werden
      setFilteredData(prevData => {
        const itemIndex = prevData.findIndex(item => item && item.id === message.id);
        if (itemIndex !== -1) {
          const updatedData = [...prevData];
          updatedData[itemIndex] = {
            ...updatedData[itemIndex],
            [message.field]: message.value,
            lead_contacted: message.value,
            lead_last_email: {
              ...updatedData[itemIndex].lead_last_email,
              timestamp: message.timestamp,
              status: message.value
            },
            updated_at: message.timestamp,
            last_contact_date: new Date(message.timestamp).toISOString(),
            _local_status_update: true
          };
          return updatedData;
        }
        return prevData;
      });
      
      setDisplayedData(prevData => {
        const itemIndex = prevData.findIndex(item => item && item.id === message.id);
        if (itemIndex !== -1) {
          const updatedData = [...prevData];
          updatedData[itemIndex] = {
            ...updatedData[itemIndex],
            [message.field]: message.value,
            lead_contacted: message.value,
            lead_last_email: {
              ...updatedData[itemIndex].lead_last_email,
              timestamp: message.timestamp,
              status: message.value
            },
            updated_at: message.timestamp,
            last_contact_date: new Date(message.timestamp).toISOString(),
            _local_status_update: true
          };
          return updatedData;
        }
        return prevData;
      });
      
      // Force-Update des gesamten Datensatzes nach kurzer Verzögerung
      setTimeout(() => {
        setFullDbData(prev => [...prev]);
        setFilteredData(prev => [...prev]);
        setDisplayedData(prev => [...prev]);
      }, 100);
      
      // Markiere als verarbeitet
      message._processed = true;
      
      // Keine UI-Benachrichtigung für Status-Änderungen
      return;
    } else if (status === 'summary') {
      // Spezielle Behandlung für Zusammenfassungen (Bulk-Mode etc.)
      setEmailNotifier({ 
        status: 'summary', 
        message, 
        scheduledTime: null,
        recipientEmail: null
      });
    } else {
      // Normale Benachrichtigungen wie bisher (nur für E-Mail-Versand)
      setEmailNotifier({ 
        status, 
        message, 
        scheduledTime: validScheduledTime,
        recipientEmail: recipientEmail || (rowData ? rowData.lead_email : null)
      });
    }
  };

  // Extrahiere Import-Namen aus den Daten
  useEffect(() => {
    if (!userId) return;

    const fetchImportNames = async () => {
      try {
        // Effizientes Auslesen der vorberechneten Import-Namens-Liste
        const importNamesRef = collection(firestore, `World/${userId}/import_names`);
        const q = query(importNamesRef, orderBy('name', 'asc'));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const names = snapshot.docs.map(doc => doc.data().name).filter(Boolean);
          setImportOptions(names);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Import-Optionen:", error);
      }
    };

    fetchImportNames();
  }, [userId, reloadFlag]); // Auch bei einem reload neu laden

  // Neue Funktion, die manuell ausgelöst wird, um die angezeigten Daten zu aktualisieren
  const updateDisplayedData = () => {
    // Die Filterung erfolgt jetzt über Firestore, daher einfach die aktuellen Daten übernehmen
    setFilteredData(fullDbData);
    setDisplayedData(fullDbData.slice(0, displayLimit));
    setDbData(fullDbData.slice(0, displayLimit));
    setHasMoreData(fullDbData.length > displayLimit);
  };

  // Firestore Count Query für die echte Gesamtanzahl
  const fetchTotalContactCount = async () => {
    if (!userId) {
      setTotalContactCount(0);
      return;
    }
    try {
      // Zähle IMMER die gesamte Collection, ohne Filter, ohne Limit
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const snapshot = await getCountFromServer(dbCollectionRef);
      setTotalContactCount(snapshot.data().count);
    } catch (error) {
      console.error("Fehler beim Zählen der Kontakte:", error);
      setTotalContactCount(null);
    }
  };

  // Count beim Mount und bei reloadFlag laden - wird jetzt durch reloadData() getriggert
  useEffect(() => {
    fetchTotalContactCount();
  }, [userId]);

  if (!userId) {
    return <p className="errorMessage">Please login to view your data.</p>;
  }

  return (
    <div className="worldDbContainer">
      {isInitialLoad && loading && <LoadingSpinner />}
      
      <WorldDbHeader 
        activeView={activeView}
        setActiveView={setActiveView}
      />

      {needsMigration ? (
        <WorldDbMigrationModal 
          userId={userId}
          onMigrationComplete={handleMigrationComplete}
        />
      ) : activeView === "settings" ? (
        <WorldSettings />
      ) : activeView === "inbox" ? (
        <WorldInbox />
      ) : activeView === "smartInbox" ? (
        <SmartInbox userId={userId} />
      ) : activeView === "offerCreator" ? (
        <OfferCreator />
      ) : activeView === "payouts" ? (
        <ComingSoon />
      ) : isInitialLoad && !loading && fullDbData.length === 0 ? (
        // Nur wenn die Datenbank komplett leer ist, zeigen wir den "Keine Daten"-Screen an
        <div className="worldDbNoData">
          <div className="worldDbNoData-content">
            <div className="worldDbNoData-icon">
              <img src="/img/salesgod_light.webp" alt="No Data" />
            </div>
            <h3 className="worldDbNoData-title">No Data Available</h3>
            <p className="worldDbNoData-description">
              Your database is currently empty. Start building your contact list by importing existing contacts or creating new ones.
            </p>
            <div className="worldDbNoData-actions">
              <button 
                className="worldDbNoData-importBtn"
                onClick={() => setShowUploader(true)}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path 
                    d="M12 4V16M7 12L12 7L17 12" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M4 20H20" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round"
                  />
                </svg>
                Import Contacts
              </button>
              <button 
                className="worldDbNoData-addBtn"
                onClick={() => setShowCreator(true)}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path 
                    d="M12 4V20M4 12H20" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round"
                  />
                </svg>
                Add Contact
              </button>
            </div>
            <div className="worldDbNoData-tips">
              <div className="tip-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span>Import Excel or CSV files with columns for "Name" and "Email" to quickly build your database.</span>
            </div>
          </div>
        </div>
      ) : (
        // Immer die Tabelle anzeigen, wenn Daten in der Datenbank vorhanden sind,
        // auch wenn die gefilterten Daten leer sind
        <div>
          {/* 
            Die Anzeige wird auf NewWorldDbTable vereinheitlicht, da WorldTableTwo eine veraltete 
            Implementierung ist und die onReload-Funktionalität nicht korrekt unterstützt. 
            Der activeView-Switch wird hier bewusst ignoriert, um die Kernfunktionalität zu gewährleisten.
          */}
          <NewWorldDbTable 
            userId={userId}
            setShowUploader={setShowUploader}
            totalDatabaseCount={totalContactCount}
            contactedToday={todayContactedCount}
            onEmailResult={handleEmailResult}
            onReload={reloadData}
            reloadFlag={reloadFlag}
          />
        </div>
      )}

      {showUploader && <WorldDbUploader onClose={() => setShowUploader(false)} onReload={reloadData} />}
      
      {showCreator && <WorldDbCreator 
        onClose={() => setShowCreator(false)} 
        initialFields={{
          lead_email: '',
          lead_firstname: '',
          lead_lastname: '',
          business_name: ''
        }}
      />}

      {/* E-Mail-Benachrichtigung am unteren Bildschirmrand */}
      <EmailSentNotifier 
        status={emailNotifier.status}
        message={emailNotifier.message}
        onClose={resetEmailNotifier}
        scheduledTime={emailNotifier.scheduledTime}
        recipientEmail={emailNotifier.recipientEmail}
      />
    </div>
  );
};

export default WorldDb;
